import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import Nav from "../Nav";
import Filters from "./newfilters";
import Modal from "../modal/Modal";
import AthleteScore from "../athlete-scores/athlete-score";
import ExportDropdown from "./exportDropdown";
import MobileLeaderboardView from "./MobileLeaderboardView";
import { useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import {
  faFilter,
  faSearch,
  faChevronDown,
  faSort,
  faSortUp,
  faSortDown,
} from "@fortawesome/free-solid-svg-icons";
import { useAxiosInstance } from "../../services/Interceptor";
import { fetchPublicEvents } from "../../services/ContactPublicService";
import {
  fetchGraduationYears,
  fetchLeaderboardData,
} from "../../services/LeaderboardService";
import LoadingSpinner from "../LoadingSpinner";
import { getHighSchoolProfile } from "../../services/HighSchoolService";
import { getBulkPublicTokens } from "../../services/PublicTokenService";
import { toast } from "react-toastify";

const positions = [
  "Guard",
  "Wide Receiver",
  "Quarterback",
  "Offensive Tackle",
  "Slot Receiver",
  "Safety",
  "Defensive End",
  "Running Back",
  "Corner",
  "Tight End",
  "Defensive Tackle",
  "Edge/Outside Linebacker",
  "Defensive Line",
];

const filters = [
  {
    label: "All",
    image: "/All.svg",
    columns: [
      "fortyYardDash",
      "benchPress",
      "verticalJump",
      "broadJump",
      "threeCone",
      "shuttle",
    ],
  },
  {
    label: "40 Yard Dash",
    image: "/40Yard.svg",
    columns: ["tenYardDash", "fortyYardDash", "twentyYardDash"],
  },
  {
    label: "Bench Press",
    image: "/BenchPress.svg",
    columns: ["benchPress"],
  },
  {
    label: "Vertical Jump",
    image: "/VerticalJump.svg",
    columns: ["verticalJump"],
  },
  { label: "Broad Jump", image: "/BroadJump.svg", columns: ["broadJump"] },
  {
    label: "Three Cone L-drill",
    image: "/ThreeCone.svg",
    columns: ["threeCone"],
  },
  {
    label: "20 Yard Shuttle",
    image: "/20Yard.svg",
    columns: ["shuttle"],
  },
];

const columnConfig = {
  rank: { label: "Position Rank", sortKey: "rank" },
  positionScore: { label: "Position Score", sortKey: "positionScore" },
  athleteName: { label: "Athlete Name", sortKey: "athleteName" },
  trackittScore: { label: "Trackit Score", sortKey: "trackittScore" },
  trackittMeasurementScore: {
    label: "Trackit Measurement Score",
    sortKey: "trackittMeasurementScore",
  },
  trackittAthleticScore: {
    label: "Trackit Athletic Score",
    sortKey: "trackittAthleticScore",
  },
  positionAthleticScore: {
    label: "Position Athletic Score",
    sortKey: "positionAthleticScore",
  },
  positionMeasurementScore: {
    label: "Position Measurement Score",
    sortKey: "positionMeasurementScore",
  },
  primaryPosition: { label: "Primary Position", sortKey: "primaryPosition" },
  offerTierName: { label: "Offer Tier", sortKey: "offerTierRating" }, // NEW COLUMN
  eventsAttended: { label: "Events", sortKey: "eventsAttended" },
  graduation: { label: "Graduation", sortKey: "graduation" },
  highSchool: { label: "High School", sortKey: "highSchool" },
  state: { label: "State", sortKey: "state" },
  height: { label: "Height", sortKey: "heightForSorting" },
  weight: { label: "Weight", sortKey: "weight" },
  wingspan: { label: "Wingspan", sortKey: "wingspan" },
  handSize: { label: "Hand", sortKey: "hand" },
  fortyYardDash: { label: "40 Yard Dash", sortKey: "fortyYardDash" },
  shuttle: { label: "20 Yard Shuttle", sortKey: "shuttle" },
  tenYardDash: { label: "10 Yard Split", sortKey: "tenYardDash" },
  twentyYardDash: { label: "20 Yard Split", sortKey: "twentyYardDash" },
  benchPress: { label: "Bench Press", sortKey: "benchPressRank" },
  verticalJump: { label: "Vertical Jump", sortKey: "verticalJump" },
  broadJump: { label: "Broad Jump", sortKey: "broadJump" },
  threeCone: { label: "Three Cone L-drill", sortKey: "threeCone" },
};

const INITIAL_FILTER_PARAMS = {
  year: [],
  position: [],
  eventIds: [],
  highSchools: [],
  states: [],
  heightFilterOperator: null,
  heightValue: 0,
  weightFilterOperator: null,
  weightValue: 0,
  wingspanFilterOperator: null,
  wingspanValue: 0,
  handFilterOperator: null,
  handValue: 0,
  trackittScoreType: null,
  trackittScoreValue: 0,
  positionScoreType: null,
  positionScoreValue: 0,
  offerTypes: [],
};

const CONTACT_EXPORT_COLUMNS = [
  { header: "Athlete Email", accessor: (contact) => contact?.athleteEmail },
  { header: "Athlete Phone", accessor: (contact) => contact?.athletePhone },
  { header: "Parent Email", accessor: (contact) => contact?.parentEmail },
  { header: "Parent Phone", accessor: (contact) => contact?.parentPhone },
  { header: "X", accessor: (contact) => contact?.twitter },
  { header: "Instagram", accessor: (contact) => contact?.instagram },
];

const hasMeasurementParts = (feet, inches) =>
  [feet, inches].some(
    (part) => part !== null && part !== undefined && part !== "",
  );

const toSafeNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getMeasurementInches = (feet, inches, fallback) => {
  if (hasMeasurementParts(feet, inches)) {
    return (toSafeNumber(feet) || 0) * 12 + (toSafeNumber(inches) || 0);
  }

  return toSafeNumber(fallback);
};

// Classifies a sort value as "missing" so it sorts to the bottom.
// 0 is intentionally treated as missing because recorded measurements
// are always positive; a zero indicates no data was captured.
const isMissingSortValue = (v) =>
  v === 0 ||
  v == null ||
  v === "" ||
  v === "0 x 0" ||
  v === "0.000 in" ||
  v === "0.000 lbs";

const formatMeasurement = (feet, inches, fallback, round = true) => {
  if (hasMeasurementParts(feet, inches)) {
    return `${toSafeNumber(feet) || 0}' ${toSafeNumber(inches) || 0}''`;
  }

  const totalInches = toSafeNumber(fallback);
  if (totalInches !== null) {
    const normalized = round ? Math.round(totalInches) : totalInches;
    const formattedFeet = Math.floor(normalized / 12);
    const formattedInches = round
      ? normalized % 12
      : Number((normalized % 12).toFixed(3));
    return `${formattedFeet}' ${formattedInches}''`;
  }

  return fallback || "0";
};

const getSortValue = (athlete, key) => {
  if (key === "heightForSorting") {
    return (
      athlete?.heightForSorting ??
      getMeasurementInches(
        athlete?.heightFt,
        athlete?.heightIn,
        athlete?.height,
      )
    );
  }

  if (key === "hand") {
    const parsed = parseFloat(athlete?.hand);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return athlete?.[key];
};

export default function Leaderboard({ highSchool }) {
  const apiInstance = useAxiosInstance();
  const location = useLocation();
  const authUser = useAuthUser();
  const role = authUser?.user?.role?.toLowerCase?.() || "";
  const isHighSchoolCoach = authUser?.user?.role === "highschool";
  const canExportContactInfo = ["admin", "university", "highschool"].includes(
    role,
  );
  const isAdmin = role === "admin";
  const isHighSchoolLeaderboard = location.pathname.startsWith(
    "/highschool/leaderboard",
  );
  const contextualHighSchool = (
    highSchool ||
    authUser?.user?.highSchool ||
    ""
  ).trim();
  const [search, setSearch] = useState("");
  const [yearDropdown, setYearDropdown] = useState(false);
  const [eventDropdown, setEventDropdown] = useState(false);
  const [filterDropdown, setFilterDropdown] = useState(false);
  const [filterSearch, setFilterSearch] = useState("");
  const [selectedYear, setSelectedYear] = useState("Select Year");
  const [selectedEvent, setSelectedEvent] = useState("Select Event");
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [tempSelectedFilters, setTempSelectedFilters] = useState([]);
  const [allSelectedFilters, setAllSelectedFilters] = useState([]);
  const [sortBy, setSortBy] = useState("rank");
  const [sortDirection, setSortDirection] = useState("asc");
  const [isMobileLeaderboardLayout, setIsMobileLeaderboardLayout] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(max-width: 767px)").matches,
  );
  const [years, setYears] = useState([]);
  const [events, setEvents] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [athleteId, setAthleteId] = useState(0);
  const [activeFilter, setActiveFilter] = useState("All");
  const [filterParams, setFilterParams] = useState(INITIAL_FILTER_PARAMS);
  const [includeGraduatedKids, setIncludeGraduatedKids] = useState(
    filterParams.includeGraduatedKids || false,
  );
  const [highSchoolProfileData, setHighSchoolProfileData] = useState();
  // const hasEventFilter =
  //   filterParams.eventIds && filterParams.eventIds.length > 0;

  const isScoreFilterActive =
    (filterParams.trackittScoreType && filterParams.trackittScoreValue > 0) ||
    (filterParams.positionScoreType && filterParams.positionScoreValue > 0);

  const isEventFilterActive =
    filterParams.eventIds && filterParams.eventIds.length > 0;

  const dropdownRefs = useRef([]);
  const fetchIdRef = useRef(0);
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isExportingContactInfo, setIsExportingContactInfo] = useState(false);
  const [isExportingLeaderboard, setIsExportingLeaderboard] = useState(false);
  const getVisibleColumns = () => {
    // base score columns always visible
    const baseScoreColumns = ["positionScore", "trackittScore"];

    // extra score columns only when score filter is active
    const extraScoreColumns = [
      "trackittMeasurementScore",
      "trackittAthleticScore",
      "positionAthleticScore",
      "positionMeasurementScore",
    ];

    const scoreColumns = isScoreFilterActive
      ? [...baseScoreColumns, ...extraScoreColumns]
      : baseScoreColumns;

    // events column only when filtered by event
    const eventColumns = isEventFilterActive ? ["eventsAttended"] : [];

    // Offer Tier column always visible
    const offerTierColumn = ["offerTierName"];
    if (activeFilter === "All") {
      return [
        "athleteName",
        "rank",
        ...scoreColumns,
        ...eventColumns,
        "primaryPosition",
        ...offerTierColumn,
        "graduation",
        "highSchool",
        "state",
        "height",
        "weight",
        "wingspan",
        "handSize",
        "tenYardDash",
        "fortyYardDash",
        "shuttle",
        "benchPress",
        "verticalJump",
        "broadJump",
        "threeCone",
      ];
    }

    const filter = filters?.find((f) => f.label === activeFilter);
    if (!filter) return Object.keys(columnConfig);

    const basicColumns = [
      "athleteName",
      "rank",
      ...scoreColumns,
      ...eventColumns,
      "primaryPosition",
      ...offerTierColumn,
      "graduation",
      "highSchool",
      "state",
      "height",
      "weight",
      "wingspan",
      "handSize",
    ];

    return [...new Set([...basicColumns, ...filter.columns])];
  };

  const visibleColumns = getVisibleColumns();

  const fetchAthletes = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    setLoading(true);
    try {
      // helper: wrap a single value into an array, but return undefined if empty
      const asNonEmptyArray = (v) =>
        v == null || v === ""
          ? undefined
          : Array.isArray(v)
            ? v.length
              ? v
              : undefined
            : [v];

      // helper: strip null/undefined/empty-array fields
      const prune = (obj) =>
        Object.fromEntries(
          Object.entries(obj).filter(
            ([, v]) =>
              v !== null &&
              v !== undefined &&
              (!Array.isArray(v) || v.length > 0),
          ),
        );

      const payload = prune({
        year: filterParams.year,
        position: filterParams.position,
        eventIds: filterParams.eventIds,
        highSchools:
          isHighSchoolLeaderboard && contextualHighSchool
            ? [contextualHighSchool]
            : filterParams.highSchools,
        states: filterParams.states,

        heightFilterOperator: filterParams.heightFilterOperator || null,
        heightValue: filterParams.heightValue ?? null,
        weightFilterOperator: filterParams.weightFilterOperator || null,
        weightValue: filterParams.weightValue ?? null,
        wingspanFilterOperator: filterParams.wingspanFilterOperator || null,
        wingspanValue: filterParams.wingspanValue ?? null,
        handFilterOperator: filterParams.handFilterOperator || null,
        handValue: filterParams.handValue ?? null,

        scoreFilterTypes: filterParams.scoreFilterTypes,
        scoreFilterOperator: filterParams.scoreFilterOperator,
        scoreValue: filterParams.scoreValue,

        trackittScoreFilterTypes: asNonEmptyArray(
          filterParams.trackittScoreType,
        ),
        trackittScoreValue:
          filterParams.trackittScoreValue !== undefined &&
          filterParams.trackittScoreValue !== null &&
          filterParams.trackittScoreValue !== ""
            ? Number(filterParams.trackittScoreValue)
            : undefined,
        includeGraduatedKids,

        positionScoreFilterTypes: asNonEmptyArray(
          filterParams.positionScoreType,
        ),
        positionScoreValue:
          filterParams.positionScoreValue !== undefined &&
          filterParams.positionScoreValue !== null &&
          filterParams.positionScoreValue !== ""
            ? Number(filterParams.positionScoreValue)
            : undefined,

        offerTypes: (filterParams.offerTypes || []).map((t) =>
          t === "With Any Offers" ? "Has Offers" : t.replace(/^With /, "Has "),
        ),
      });

      const data = await fetchLeaderboardData(apiInstance, payload);
      if (fetchId !== fetchIdRef.current) return;
      setAthletes(Array.isArray(data) ? data : []);
    } catch (error) {
      if (fetchId !== fetchIdRef.current) return;
      console.error("Error fetching athletes:", error);
      setAthletes([]);
    } finally {
      if (fetchId !== fetchIdRef.current) return;
      setLoading(false);
    }
  }, [
    apiInstance,
    contextualHighSchool,
    filterParams,
    includeGraduatedKids,
    isHighSchoolLeaderboard,
  ]);

  useEffect(() => {
    fetchAthletes();
  }, [fetchAthletes]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const mediaQuery = window.matchMedia("(max-width: 767px)");
    const updateLayout = (event) => {
      setIsMobileLeaderboardLayout(event.matches);
    };

    setIsMobileLeaderboardLayout(mediaQuery.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", updateLayout);
    } else {
      mediaQuery.addListener(updateLayout);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", updateLayout);
      } else {
        mediaQuery.removeListener(updateLayout);
      }
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !dropdownRefs.current.some((ref) => ref && ref.contains(event.target))
      ) {
        setYearDropdown(false);
        setEventDropdown(false);
        setFilterDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchHighSchoolProfile = async () => {
      const response = await getHighSchoolProfile(apiInstance);
      setHighSchoolProfileData(response);
    };

    if (authUser.user.role === "highschool") {
      fetchHighSchoolProfile();
    }
  }, [apiInstance, authUser.user.role]);

  const sortedFilteredAthletes = useMemo(() => {
    const normalize = (str) =>
      (str ?? "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");

    const tokens = normalize(search).trim().split(/\s+/).filter(Boolean);

    return athletes
      .filter((a) => {
        if (!tokens.length) return true;
        const haystack = normalize(a?.athleteName);
        return tokens.every((token) => haystack.includes(token));
      })
      .sort((a, b) => {
        // Special sorting for Offer Tier column
        if (sortBy === "offerTierName") {
          const aValue = a?.offerTierRating;
          const bValue = b?.offerTierRating;
          const aIsZero = aValue === 0 || aValue == null;
          const bIsZero = bValue === 0 || bValue == null;
          if (aIsZero && !bIsZero) return 1;
          if (!aIsZero && bIsZero) return -1;
          if (aIsZero && bIsZero) return 0;
          if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
          if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
          return 0;
        }
        // Default sorting
        const aValue = getSortValue(a, sortBy);
        const bValue = getSortValue(b, sortBy);
        const aIsZero = isMissingSortValue(aValue);
        const bIsZero = isMissingSortValue(bValue);

        if (aIsZero && !bIsZero) return 1;
        if (!aIsZero && bIsZero) return -1;
        if (aIsZero && bIsZero) return 0;

        if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
        if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
        return 0;
      });
  }, [athletes, search, sortBy, sortDirection]);

  const resetFilters = () => {
    setSelectedYear("Select Year");
    setSelectedEvent("Select Event");
    setSelectedFilters([]);
    setTempSelectedFilters([]);
    setSearch("");
    setSortBy("rank");
    setSortDirection("asc");
    setActiveFilter("All");
    setFilterParams(INITIAL_FILTER_PARAMS);
    setAllSelectedFilters([]);
    setIncludeGraduatedKids(false);
  };

  const clearFilter = (filterToRemove) => {
    const newSelectedFilters = allSelectedFilters.filter(
      (f) => f.value !== filterToRemove.value,
    );
    setAllSelectedFilters(newSelectedFilters);

    let updatedFilters = { ...filterParams };

    switch (filterToRemove.type) {
      case "year":
        updatedFilters.year = updatedFilters.year.filter(
          (y) => y !== parseInt(filterToRemove.original),
        );
        break;
      case "position":
        updatedFilters.position = updatedFilters.position.filter(
          (p) => p !== filterToRemove.original,
        );
        break;
      case "eventIds":
        updatedFilters.eventIds = updatedFilters.eventIds.filter((e) => {
          const eId = typeof e === "object" ? e.value || e.id : e;
          return eId !== filterToRemove.original;
        });
        break;
      case "highSchools":
        updatedFilters.highSchools = updatedFilters.highSchools.filter(
          (s) => s !== filterToRemove.original,
        );
        break;
      case "states":
        updatedFilters.states = updatedFilters.states.filter(
          (s) =>
            s !== filterToRemove.original.split("(")[0].replace(")", "").trim(),
        );
        break;
      case "height":
        updatedFilters.heightValue = 0;
        updatedFilters.heightFilterOperator = "";
        break;
      case "weight":
        updatedFilters.weightValue = 0;
        updatedFilters.weightFilterOperator = "";
        break;
      case "wingspan":
        updatedFilters.wingspanValue = 0;
        updatedFilters.wingspanFilterOperator = "";
        break;
      case "hand":
        updatedFilters.handValue = 0;
        updatedFilters.handFilterOperator = "";
        break;
      case "score":
        updatedFilters.scoreFilterTypes = [];
        updatedFilters.scoreValue = 0;
        updatedFilters.scoreFilterOperator = "";
        break;
      case "trackittScore":
        updatedFilters.trackittScoreType = null;
        updatedFilters.trackittScoreValue = 0;
        break;
      case "positionScore":
        updatedFilters.positionScoreType = null;
        updatedFilters.positionScoreValue = 0;
        break;
      case "offerTypes":
        updatedFilters.offerTypes = updatedFilters.offerTypes.filter(
          (x) => x !== filterToRemove.original,
        );
        break;
      case "includeGraduated":
        setIncludeGraduatedKids(false);
        break;
      default:
        break;
    }

    setFilterParams(updatedFilters);
  };

  const clearAll = () => {
    setTempSelectedFilters([]);
    setAllSelectedFilters([]);
    setSelectedFilters([]);
    setSelectedYear("Select Year");
    setFilterParams(INITIAL_FILTER_PARAMS);
    setIncludeGraduatedKids(false);
  };

  const handleViewClick = useCallback((athleteId) => {
    setAthleteId(athleteId);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = () => {
    setAthleteId(0);
    setIsModalOpen(false);
  };

  const escapeCsvValue = (value, emptyValue = "") => {
    const normalized =
      value === null || value === undefined || value === ""
        ? emptyValue
        : value;
    return `"${String(normalized).replace(/"/g, '""')}"`;
  };

  const downloadCsvFile = (content, fileName) => {
    const blob = new Blob([content], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getLeaderboardExportHeaders = (includeProfileLink = false) => [
    ...visibleColumns.map((column) => columnConfig[column].label),
    ...(includeProfileLink ? ["Profile Link"] : []),
  ];

  const getProfileLinkValue = (athlete, profileLinkMap) => {
    const token = profileLinkMap[athlete.userId];
    return token ? `${process.env.REACT_APP_URL}/public-link/${token}` : "";
  };

  const fetchProfileLinkMap = async () => {
    const userIds = sortedFilteredAthletes
      .map((athlete) => athlete.userId)
      .filter((userId) => userId !== null && userId !== undefined);

    if (!userIds.length) return {};

    return (await getBulkPublicTokens(apiInstance, userIds)) || {};
  };

  const getLeaderboardExportRows = (
    profileLinkMap = {},
    includeProfileLink = false,
  ) =>
    sortedFilteredAthletes.map((athlete) => [
      ...visibleColumns.map((column) => {
        if (column === "height") {
          return formatMeasurement(
            athlete.heightFt,
            athlete.heightIn,
            athlete.height,
          );
        }

        if (column === "wingspan") {
          return formatMeasurement(
            athlete.wingSpanFt,
            athlete.wingSpanIn,
            athlete.wingspan,
            false,
          );
        }

        if (column === "handSize") return athlete.hand ?? "";
        if (column === "tenYardDash") return athlete.tenYardDash ?? "";
        if (column === "twentyYardDash") return athlete.twentyYardDash ?? "";

        return athlete[column] ?? "";
      }),
      ...(includeProfileLink
        ? [getProfileLinkValue(athlete, profileLinkMap)]
        : []),
    ]);

  const getLeaderboardExportContactInfoRows = (
    profileLinkMap = {},
    includeProfileLink = false,
  ) =>
    sortedFilteredAthletes.map((athlete) => [
      ...visibleColumns.map((column) => {
        if (column === "height") {
          return formatMeasurement(
            athlete.heightFt,
            athlete.heightIn,
            athlete.height,
          );
        }

        if (column === "wingspan") {
          return formatMeasurement(
            athlete.wingSpanFt,
            athlete.wingSpanIn,
            athlete.wingspan,
            false,
          );
        }

        if (column === "handSize") return athlete.hand ?? "";
        if (column === "tenYardDash") return athlete.tenYardDash ?? "";
        if (column === "twentyYardDash") return athlete.twentyYardDash ?? "";

        return athlete[column] ?? "";
      }),
      ...CONTACT_EXPORT_COLUMNS.map(({ accessor }) => accessor(athlete) ?? ""),
      ...(includeProfileLink
        ? [getProfileLinkValue(athlete, profileLinkMap)]
        : []),
    ]);
  const buildCsvContent = (headers, rows, emptyValue = "") =>
    [headers, ...rows]
      .map((row) =>
        row.map((value) => escapeCsvValue(value, emptyValue)).join(","),
      )
      .join("\n");

  const exportLeaderboardCsv = async () => {
    if (!sortedFilteredAthletes.length || isExportingLeaderboard) return;

    setIsExportingLeaderboard(true);
    try {
      const profileLinkMap = isAdmin ? await fetchProfileLinkMap() : {};

      const headers = getLeaderboardExportHeaders(isAdmin);
      const rows = getLeaderboardExportRows(profileLinkMap, isAdmin);
      const csvContent = buildCsvContent(headers, rows);

      downloadCsvFile(csvContent, "Export leaderboard.csv");
    } catch (error) {
      console.error("Error exporting leaderboard:", error);
      toast.error("Unable to export leaderboard right now.");
    } finally {
      setIsExportingLeaderboard(false);
    }
  };

  const exportContactInfoCsv = async () => {
    if (!sortedFilteredAthletes.length || isExportingContactInfo) return;

    setIsExportingContactInfo(true);

    try {
      const successfulCount = sortedFilteredAthletes.length;

      if (successfulCount === 0) {
        toast.error("Unable to export contact info right now.");
        return;
      }

      const profileLinkMap = isAdmin ? await fetchProfileLinkMap() : {};

      const headers = [
        ...getLeaderboardExportHeaders(),
        ...CONTACT_EXPORT_COLUMNS.map(({ header }) => header),
        ...(isAdmin ? ["Profile Link"] : []),
      ];

      const rows = getLeaderboardExportContactInfoRows(profileLinkMap, isAdmin);
      const csvContent = buildCsvContent(headers, rows);

      downloadCsvFile(csvContent, "Export with Contact Info.csv");

      toast.success(
        `Exported contact info for ${successfulCount} athlete${successfulCount > 1 ? "s" : ""}.`,
      );
    } catch (error) {
      console.error("Error exporting athlete contact info:", error);
      toast.error("Unable to export contact info right now.");
    } finally {
      setIsExportingContactInfo(false);
    }
  };

  const handleApplyFilters = (filters) => {
    const eventIds = filters.eventIds.map((event) =>
      typeof event === "object" ? event.value || event.id : event,
    );

    const apiFilters = {
      year: filters.year?.map((y) => parseInt(y)),
      position: filters.position,
      eventIds,
      highSchools: filters.highSchools,
      states: filters.states?.map((s) =>
        s.split("(")[0].replace(")", "").trim(),
      ),
      heightFilterOperator: filters.heightFilterOperator || null,
      heightValue: parseFloat(filters.heightValue) || 0,
      weightFilterOperator: filters.weightFilterOperator || null,
      weightValue: parseFloat(filters.weightValue) || 0,
      wingspanFilterOperator: filters.wingspanFilterOperator || null,
      wingspanValue: parseFloat(filters.wingspanValue) || 0,
      handFilterOperator: filters.handFilterOperator || null,
      handValue: parseFloat(filters.handValue) || 0,

      // NEW score filters (backend will interpret as >=)
      trackittScoreType: filters.trackittScoreType || null,
      trackittScoreValue: parseFloat(filters.trackittScoreValue) || 0,
      positionScoreType: filters.positionScoreType || null,
      positionScoreValue: parseFloat(filters.positionScoreValue) || 0,
      offerTypes: filters.offerTypes || [],
    };

    setFilterParams(apiFilters);

    const allSelected = [
      ...filters.year.map((y) => ({
        type: "year",
        value: `Year: ${y}`,
        original: y,
      })),
      ...filters.position.map((p) => ({
        type: "position",
        value: `Position: ${p}`,
        original: p,
      })),
      ...filters.eventIds.map((e) => {
        const eventName =
          typeof e === "object" ? e.label || e.name : `Event ${e}`;
        const eventDate = typeof e === "object" && e.date ? e.date : "";
        const eventId = typeof e === "object" ? e.value || e.id : e;
        return {
          type: "eventIds",
          value: eventDate
            ? `Event: ${eventDate} ${eventName}`
            : `Event: ${eventName}`,
          original: eventId,
        };
      }),
      ...filters.highSchools.map((s) => ({
        type: "highSchools",
        value: `School: ${s}`,
        original: s,
      })),
      ...filters.states.map((s) => ({
        type: "states",
        value: `State: ${s.split("(")[0].replace(")", "").trim()}`,
        original: s,
      })),
      ...(filters.heightValue
        ? [
            {
              type: "height",
              value: `Height: ${filters.heightFilterOperator} ${filters.heightValue} inches`,
              original: filters.heightValue,
              operator: filters.heightFilterOperator,
            },
          ]
        : []),
      ...(filters.weightValue
        ? [
            {
              type: "weight",
              value: `Weight: ${filters.weightFilterOperator} ${filters.weightValue} lbs`,
              original: filters.weightValue,
              operator: filters.weightFilterOperator,
            },
          ]
        : []),
      ...(filters.wingspanValue
        ? [
            {
              type: "wingspan",
              value: `Wingspan: ${filters.wingspanFilterOperator} ${filters.wingspanValue} inches`,
              original: filters.wingspanValue,
              operator: filters.wingspanFilterOperator,
            },
          ]
        : []),
      ...(filters.handValue
        ? [
            {
              type: "hand",
              value: `Hand: ${filters.handFilterOperator} ${filters.handValue} inches`,
              original: filters.handValue,
              operator: filters.handFilterOperator,
            },
          ]
        : []),

      // NEW chips for score filters
      ...(filters.trackittScoreType && filters.trackittScoreValue
        ? [
            {
              type: "trackittScore",
              value: `Trackit: ${filters.trackittScoreType} ≥ ${filters.trackittScoreValue}`,
              original: filters.trackittScoreType,
              scoreValue: filters.trackittScoreValue,
            },
          ]
        : []),
      ...(filters.positionScoreType && filters.positionScoreValue
        ? [
            {
              type: "positionScore",
              value: `Position: ${filters.positionScoreType} ≥ ${filters.positionScoreValue}`,
              original: filters.positionScoreType,
              scoreValue: filters.positionScoreValue,
            },
          ]
        : []),
      ...(filters.offerTypes || []).map((t) => ({
        type: "offerTypes",
        value: `Offer Types: ${t}`,
        original: t,
      })),
      ...(filters.includeGraduatedKids
        ? [
            {
              type: "includeGraduated",
              value: "Include Graduated",
              original: true,
            },
          ]
        : []),
    ];

    setAllSelectedFilters(allSelected);
  };

  const columnRenderers = useMemo(
    () => ({
      offerTierName: (athlete) => (
        <td key="offerTierName" className="p-2 text-center">
          {athlete.offerTierName || ""}
        </td>
      ),
      rank: (athlete) => (
        <td key="rank" className="p-2 text-center">
          {athlete.rank}
        </td>
      ),
      positionScore: (athlete) => (
        <td
          key="positionScore"
          className="p-2 text-gray-800 font-semibold"
          onClick={() => handleViewClick(athlete.userId)}
        >
          {Math.round(athlete.positionScore)}
        </td>
      ),
      trackittScore: (athlete) => (
        <td
          key="trackittScore"
          className="p-2 text-gray-800 font-semibold"
          onClick={() => handleViewClick(athlete.userId)}
        >
          {Math.round(athlete.trackittScore)}
        </td>
      ),
      trackittMeasurementScore: (athlete) => (
        <td
          key="trackittMeasurementScore"
          className="p-2 text-gray-800 font-semibold"
          onClick={() => handleViewClick(athlete.userId)}
        >
          {Math.round(athlete.trackittMeasurementScore)}
        </td>
      ),
      trackittAthleticScore: (athlete) => (
        <td
          key="trackittAthleticScore"
          className="p-2 text-gray-800 font-semibold"
          onClick={() => handleViewClick(athlete.userId)}
        >
          {Math.round(athlete.trackittAthleticScore)}
        </td>
      ),
      positionAthleticScore: (athlete) => (
        <td
          key="positionAthleticScore"
          className="p-2 text-gray-800 font-semibold"
          onClick={() => handleViewClick(athlete.userId)}
        >
          {Math.round(athlete.positionAthleticScore)}
        </td>
      ),
      positionMeasurementScore: (athlete) => (
        <td
          key="positionMeasurementScore"
          className="p-2 text-gray-800 font-semibold"
          onClick={() => handleViewClick(athlete.userId)}
        >
          {Math.round(athlete.positionMeasurementScore)}
        </td>
      ),
      athleteName: (athlete) => (
        <td
          key="athleteName"
          className="p-2 text-gray-800 font-semibold"
          onClick={() => handleViewClick(athlete.userId)}
        >
          {athlete.athleteName}
        </td>
      ),
      primaryPosition: (athlete) => (
        <td key="primaryPosition" className="p-2 text-left">
          {athlete.primaryPosition}
        </td>
      ),
      eventsAttended: (athlete) => (
        <td key="eventsAttended" className="p-2 text-left">
          <span className="table-truncate">{athlete.eventsAttended}</span>
        </td>
      ),
      graduation: (athlete) => (
        <td key="graduation" className="p-2 text-center">
          {athlete.graduation}
        </td>
      ),
      highSchool: (athlete) => (
        <td key="highSchool" className="p-2 text-left">
          {athlete.highSchool}
        </td>
      ),
      state: (athlete) => (
        <td key="state" className="p-2 text-center">
          {athlete.state}
        </td>
      ),
      height: (athlete) => (
        <td key="height" className="p-2 text-center">
          {formatMeasurement(
            athlete.heightFt,
            athlete.heightIn,
            athlete.height,
          )}
        </td>
      ),
      weight: (athlete) => (
        <td key="weight" className="p-2 text-center">
          {athlete.weight} lbs
        </td>
      ),
      wingspan: (athlete) => (
        <td key="wingspan" className="p-2 text-center">
          {formatMeasurement(
            athlete.wingSpanFt,
            athlete.wingSpanIn,
            athlete.wingspan,
            false,
          )}
        </td>
      ),
      handSize: (athlete) => (
        <td key="handSize" className="p-2 text-center">
          {athlete.hand ?? ""}
        </td>
      ),
      fortyYardDash: (athlete) => (
        <td key="fortyYardDash" className="p-2 text-center">
          {athlete.fortyYardDash}
        </td>
      ),
      tenYardDash: (athlete) => (
        <td key="tenYardDash" className="p-2 text-center">
          {athlete.tenYardDash ?? ""}
        </td>
      ),
      twentyYardDash: (athlete) => (
        <td key="twentyYardDash" className="p-2 text-center">
          {athlete.twentyYardDash || ""}
        </td>
      ),
      benchPress: (athlete) => (
        <td key="benchPress" className="p-2 text-center">
          {athlete.benchPress}
        </td>
      ),
      verticalJump: (athlete) => (
        <td key="verticalJump" className="p-2 text-center">
          {athlete.verticalJump}
        </td>
      ),
      broadJump: (athlete) => (
        <td key="broadJump" className="p-2 text-center">
          {athlete.broadJump}
        </td>
      ),
      threeCone: (athlete) => (
        <td key="threeCone" className="p-2 text-center">
          {athlete.threeCone}
        </td>
      ),
      shuttle: (athlete) => (
        <td key="shuttle" className="p-2 text-center">
          {athlete.shuttle}
        </td>
      ),
    }),
    [handleViewClick],
  );

  const renderSortableHeader = (label, key, headerKey) => {
    const isActive = sortBy === key;
    let icon = faSort;
    if (isActive) icon = sortDirection === "asc" ? faSortUp : faSortDown;

    return (
      <th
        key={headerKey}
        className="p-2 cursor-pointer select-none"
        onClick={() => {
          if (sortBy === key) {
            setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
          } else {
            setSortBy(key);
            setSortDirection("asc");
          }
        }}
      >
        <div className="flex items-center gap-2 text-sm font-semibold">
          {label}
          <FontAwesomeIcon icon={icon} className="text-xs text-gray-600" />
        </div>
      </th>
    );
  };

  return (
    <>
      {!isHighSchoolLeaderboard && <Nav fixedMobile />}
      {!isHighSchoolLeaderboard && <div className="h-16 lg:hidden" />}
      <div
        className={
          isHighSchoolLeaderboard
            ? "px-0 py-0 sm:py-0"
            : "px-0 pb-0 md:px-4 lg:py-10"
        }
      >
        {!isHighSchoolLeaderboard && !isMobileLeaderboardLayout && (
          <div className="text-center">
            <div className="text-xl sm:text-3xl text-white uppercase pb-2">
              Leaderboard
            </div>
            <p className="text-sm md:text-lg text-white">
              Compare top-performing athletes across all events
            </p>
          </div>
        )}
        <div
          className={
            isHighSchoolLeaderboard
              ? "w-full min-h-dvh bg-white rounded-xl mx-0 mt-0 p-3 text-sm shadow-lg"
              : "max-w-8xl min-h-dvh bg-white rounded-none md:rounded-xl mx-0 md:mx-4 xl:mx-16 mt-0 md:mt-5 p-3 text-sm"
          }
        >
          {isHighSchoolLeaderboard && !isMobileLeaderboardLayout && (
            <div className="text-xl mb-5 uppercase">
              {highSchoolProfileData?.highSchool} Leaderboard
            </div>
          )}
          {isMobileLeaderboardLayout ? (
            <MobileLeaderboardView
              activeFilter={activeFilter}
              canExportContactInfo={canExportContactInfo}
              columnFilters={filters}
              disabledExport={
                isExportingContactInfo ||
                isExportingLeaderboard ||
                !sortedFilteredAthletes.length
              }
              filterParams={filterParams}
              handleViewClick={handleViewClick}
              includeGraduatedKids={includeGraduatedKids}
              isExportingContactInfo={isExportingContactInfo}
              isHighSchoolCoach={isHighSchoolCoach}
              isHighSchoolLeaderboard={isHighSchoolLeaderboard}
              loading={loading}
              onApplyFilters={handleApplyFilters}
              onClearFilters={resetFilters}
              onExport={exportLeaderboardCsv}
              onExportContactInfo={exportContactInfoCsv}
              search={search}
              setActiveFilter={setActiveFilter}
              setIncludeGraduatedKids={setIncludeGraduatedKids}
              setSearch={setSearch}
              setSortBy={setSortBy}
              setSortDirection={setSortDirection}
              sortBy={sortBy}
              sortDirection={sortDirection}
              sortedFilteredAthletes={sortedFilteredAthletes}
            />
          ) : (
            <>
              {/* Top Year Dropdown */}
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <div className="">
                  <Filters
                    onApplyFilters={handleApplyFilters}
                    onClearFilters={resetFilters}
                    filterParams={filterParams}
                    includeGraduatedKids={includeGraduatedKids}
                    setIncludeGraduatedKids={setIncludeGraduatedKids}
                    isHighSchoolLeaderboard={isHighSchoolLeaderboard}
                    isHighSchoolCoach={isHighSchoolCoach}
                  />
                  <div className="flex gap-4">
                    {/* Year Dropdown */}
                    <div
                      className="relative hidden"
                      ref={(el) => (dropdownRefs.current[0] = el)}
                    >
                      <button
                        onClick={async () => {
                          let yearsLst =
                            await fetchGraduationYears(apiInstance);
                          setYears(yearsLst);
                          setYearDropdown(!yearDropdown);
                        }}
                        className="border border-gray-300 rounded-md px-3 py-2 flex items-center gap-2 "
                      >
                        {selectedYear} <FontAwesomeIcon icon={faChevronDown} />
                      </button>
                      {yearDropdown && (
                        <ul className="absolute z-10 mt-1 bg-white border rounded-md shadow-lg w-40">
                          {years.map((year) => (
                            <li
                              key={year}
                              onClick={async () => {
                                setSelectedYear(year);
                                setYearDropdown(false);
                                setAllSelectedFilters([
                                  year,
                                  ...tempSelectedFilters,
                                ]);
                              }}
                              className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                            >
                              {year}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    {/* Event Dropdown */}
                    <div
                      className="relative hidden"
                      ref={(el) => (dropdownRefs.current[1] = el)}
                    >
                      <button
                        onClick={async () => {
                          try {
                            let eventsLst =
                              await fetchPublicEvents(apiInstance);
                            setEvents(eventsLst);
                            setEventDropdown(!eventDropdown);
                          } catch (error) {
                            toast.error("Failed to load events", {
                              position: "top-right",
                            });
                          }
                        }}
                        className="border border-gray-300 rounded-md px-3 py-2 flex items-center gap-2 text-sm cursor-pointer"
                      >
                        {selectedEvent} <FontAwesomeIcon icon={faChevronDown} />
                      </button>
                      {eventDropdown && (
                        <ul className="absolute z-10 mt-1 bg-white border rounded-md shadow-lg w-64 max-h-60 overflow-y-auto">
                          {events.map((event) => (
                            <li
                              key={event.id}
                              onClick={async () => {
                                setSelectedEvent(event.name);
                                setEventDropdown(false);
                              }}
                              className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                            >
                              {event.name}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
                <div className="-mt-16 sm:mt-0 mb-0">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 relative right-0 md:right-8 xl:right-12">
                    <div className=" text-right">
                      <ExportDropdown
                        canExportContactInfo={canExportContactInfo}
                        disabled={
                          isExportingContactInfo ||
                          isExportingLeaderboard ||
                          !sortedFilteredAthletes.length
                        }
                        isExportingContactInfo={isExportingContactInfo}
                        onExport={exportLeaderboardCsv}
                        onExportContactInfo={exportContactInfoCsv}
                      />
                      {/* Filter Dropdown */}
                      <div
                        className="relative hidden"
                        ref={(el) => (dropdownRefs.current[2] = el)}
                      >
                        <button
                          onClick={() => {
                            setTempSelectedFilters([...selectedFilters]);
                            setFilterSearch("");
                            setFilterDropdown(!filterDropdown);
                          }}
                          className="bg-gray-200 px-3 py-2 rounded-lg flex items-center gap-2 text-sm"
                        >
                          <FontAwesomeIcon icon={faFilter} /> Filter by position
                        </button>
                        {filterDropdown && (
                          <div className="absolute right-0 mt-2 bg-white border rounded-md shadow-lg p-4 z-20 w-72 max-h-80 flex flex-col">
                            <input
                              type="text"
                              placeholder="Search positions..."
                              value={filterSearch}
                              onChange={(e) => setFilterSearch(e.target.value)}
                              className="mb-3 px-3 py-2 border border-gray-300 rounded-md text-sm"
                            />
                            <div className="overflow-y-auto flex-1">
                              {positions
                                .filter((pos) =>
                                  pos
                                    .toLowerCase()
                                    .includes(filterSearch.toLowerCase()),
                                )
                                .map((position) => (
                                  <label
                                    key={position}
                                    className="flex items-center space-x-2 mb-2"
                                  >
                                    <input
                                      className="w-4 h-4"
                                      type="checkbox"
                                      checked={tempSelectedFilters.includes(
                                        position,
                                      )}
                                      onChange={() => {
                                        setTempSelectedFilters((prev) =>
                                          prev.includes(position)
                                            ? prev.filter((p) => p !== position)
                                            : [...prev, position],
                                        );
                                        setAllSelectedFilters((prev) =>
                                          prev.includes(position)
                                            ? prev.filter((p) => p !== position)
                                            : [...prev, position],
                                        );
                                      }}
                                    />
                                    <span>{position}</span>
                                  </label>
                                ))}
                            </div>
                            <div className="mt-3 flex justify-end space-x-2">
                              <button
                                onClick={() => {
                                  setTempSelectedFilters([...selectedFilters]);
                                  setFilterDropdown(false);
                                }}
                                className="px-3 py-1 border border-gray-400 rounded-md text-sm"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={async () => {
                                  setSelectedFilters(tempSelectedFilters);
                                  setFilterDropdown(false);
                                }}
                                className="px-3 py-1 bg-red-500 text-white rounded-md text-sm"
                              >
                                Apply
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className=" text-right">
                      <div className="flex gap-4 items-center">
                        {/* Search input */}
                        <div className="relative">
                          <FontAwesomeIcon
                            icon={faSearch}
                            className="absolute top-3 left-3 text-gray-400 text-sm"
                          />
                          <input
                            type="text"
                            placeholder="Search player name"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-10 pr-4 py-2 border border-gray-300 rounded-md"
                          />
                        </div>
                        {/* Clear filters */}
                        <button
                          onClick={resetFilters}
                          className="text-sm text-blue-600 hover:underline px-2 py-2 border border-blue-500 rounded-md whitespace-nowrap"
                        >
                          Clear All Filters
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Filters */}
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 justify-between items-center">
                <div className="pt-2">
                  <span className="text-md font-medium">Filters:</span>
                  <div
                    className="flex items-center space-x-2 mb-4"
                    role="region"
                    aria-label="Selected filters"
                  >
                    <div className="flex flex-wrap">
                      {allSelectedFilters.length > 0 ? (
                        allSelectedFilters.map((filter, index) => (
                          <div
                            key={index}
                            className=" bg-gray-200 text-gray-700 rounded-full px-3 py-1 text-sm mb-1 mr-1"
                          >
                            {filter.value}
                            <button
                              onClick={() => clearFilter(filter)}
                              aria-label={`Remove filter ${filter.value}`}
                              className="ml-2 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full"
                            >
                              ×
                            </button>
                          </div>
                        ))
                      ) : (
                        <span className="text-gray-500 italic">
                          No filters selected
                        </span>
                      )}
                      {allSelectedFilters.length > 0 && (
                        <button
                          onClick={clearAll}
                          className="ml-4 text-sm text-red-600 hover:underline focus:outline-none focus:ring-2 focus:ring-red-500 rounded"
                        >
                          Clear All
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                <div className="mb-4">
                  <div className="grid gap-2">
                    <div className="">
                      <span className="text-md font-medium">
                        Columns Filters:
                      </span>
                    </div>
                    <div className="w-full  overflow-auto">
                      <div className="flex border overflow-auto rounded-lg shadow-sm divide-x divide-gray-300 justify-between">
                        {filters.map((item, index) => {
                          const isActive = item.label === activeFilter;
                          const baseClasses =
                            "flex flex-col items-center px-3 py-2 text-sm cursor-pointer text-center";
                          return (
                            <div
                              key={index}
                              onClick={() => setActiveFilter(item.label)}
                              className={`${baseClasses} ${
                                isActive
                                  ? "bg-blue-50 text-blue-600"
                                  : "bg-white text-gray-800 hover:bg-gray-100"
                              }`}
                            >
                              <img
                                src={item.image}
                                alt={item.label}
                                className="h-10 mb-1"
                              />
                              <span className="leading-tight break-words">
                                {item.label}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Table */}
              <div
                className={`overflow-auto w-full ${
                  isHighSchoolLeaderboard ? "max-h-[62vh]" : "max-h-[80vh]"
                }`}
                style={
                  isHighSchoolLeaderboard
                    ? { width: "calc(100vw - 370px)" }
                    : undefined
                }
              >
                <table className="min-w-full text-sm text-left">
                  <thead className="bg-red-100 text-gray-800">
                    <tr className="sticky top-0 z-10 bg-red-100">
                      <th className="p-2 text-center">#</th>
                      {visibleColumns.map(
                        (column) =>
                          columnConfig[column] &&
                          renderSortableHeader(
                            columnConfig[column].label,
                            columnConfig[column].sortKey,
                            column,
                          ),
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td
                          colSpan={visibleColumns.length + 1}
                          className="p-4 text-center"
                        >
                          <div className="flex justify-center py-6">
                            <LoadingSpinner />
                          </div>
                        </td>
                      </tr>
                    ) : sortedFilteredAthletes.length === 0 ? (
                      <tr>
                        <td
                          colSpan={visibleColumns.length + 1}
                          className="p-6 text-center"
                        >
                          <div className="flex flex-col items-center justify-center text-gray-600">
                            <img
                              className="h-10 md:h-10 xl:h-16 lg:h-16 mb-4"
                              alt="No Result Found"
                              src="/no-data.png"
                            />
                            <h1 className="text-xl font-semibold text-gray-800 mb-1">
                              No Result Found
                            </h1>
                            <p className="text-gray-600 text-base">
                              Try adjusting the filters to find what you're
                              looking for.
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      sortedFilteredAthletes.map((athlete, index) => (
                        <tr
                          key={index}
                          className="border-b text-gray-600 hover:bg-red-50 cursor-pointer"
                        >
                          <td className="p-2 text-center">{index + 1}</td>
                          {visibleColumns.map((column) => {
                            const renderer = columnRenderers[column];
                            return renderer ? renderer(athlete) : null;
                          })}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
      <Modal isOpen={isModalOpen} onClose={handleCloseModal}>
        <AthleteScore
          userId={athleteId}
          showOffers={true}
          showTrackittScore={true}
          showPositionScore={true}
          searchEvent={false}
          showProfile={true}
          showRank={true}
          showLikes={true}
        />
      </Modal>
    </>
  );
}
